import { and, eq, inArray, isNull, or } from "drizzle-orm";
import { redirect } from "react-router";
import { z } from "zod";
import { PageEditor } from "~/components/site/PageEditor";
import { pg } from "~/pg.server";
import { useT, useTurl } from "~/util/Lang";
import { $LOADER, $PAGE } from "~/util/Loaders";
import { loadMarkdown } from "~/util/Markdown.server";
import { logPageView } from "~/util/stats";
import { getTitle } from "~/util/wiki";

const Params = z.object({ pageId: z.string() });

$PAGE({
  path: "wiki/:pageId/edit",
  loader: $LOADER?.(async (cx) => {
    // Log
    logPageView(cx);
    //
    const params = Params.parse(cx.params);

    if (!cx.req.sess) {
      return redirect(`/login?r=/wiki/${params.pageId}/edit`);
    }

    const page = await pg.db
      .select({
        id: pg.mdPages.id,
        slug: pg.mdPages.wikiSlug,
        game: pg.mdPages.game,
        draftId: pg.mdPages.draftId,
        createdAt: pg.mdPages.createdAt,
        revision: {
          id: pg.mdRevisions.id,
          titleEn: pg.mdRevisions.titleEn,
          contentEn: pg.mdRevisions.contentEn,
          titleRu: pg.mdRevisions.titleRu,
          contentRu: pg.mdRevisions.contentRu,
          createdAt: pg.mdRevisions.createdAt,
          images: pg.mdRevisions.images,
        },
      })
      .from(pg.mdPages)
      .where(
        and(
          eq(pg.mdPages.id, parseInt(params.pageId)),
          isNull(pg.mdPages.npcName),
          isNull(pg.mdPages.itemName),
          isNull(pg.mdPages.locName),
          isNull(pg.mdPages.questId),
          isNull(pg.mdPages.skillId),
          isNull(pg.mdPages.draftId)
        )
      )
      .innerJoin(pg.mdRevisions, eq(pg.mdRevisions.id, pg.mdPages.revisionId))
      .limit(1)
      .then((res) => res[0]);

    console.log(page);

    const images = await pg.db
      .select({ id: pg.mdImages.id, path: pg.mdImages.path })
      .from(pg.mdImages)
      .where(
        and(
          eq(pg.mdImages.pageId, page.id),
          inArray(pg.mdImages.id, page.revision.images ?? [])
        )
      );

    if (!page) {
      return redirect("/wiki");
    }

    return {
      authUser: cx.req.sess?.user,
      page: {
        ...page,
        images,
        content: {
          en: await loadMarkdown(
            page.revision.contentEn ?? "",
            cx.gd.all_gd!,
            cx.realm.lang,
            cx.realm.game
          ),
          ru: await loadMarkdown(
            page.revision.contentRu ?? "",
            cx.gd.all_gd!,
            cx.realm.lang,
            cx.realm.game
          ),
        },
      },
    };
  }),

  meta: (args) => {
    if (!args.data?.page) {
      return {
        title: "Wiki Editor - L2Hub.info",
        canonicalPath: "/wiki/edit",
      };
    }

    const title = args.t(
      getTitle(args.data.page.revision.titleEn, args.data.page.revision.titleRu)
    );

    return {
      title: `${args.t({
        en: "Page Editor",
        ru: "Редактор страницы",
      })} - ${title} - ${args.t({
        en: "Wiki",
        ru: "Wiki",
      })} - L2Hub.info`,
      canonicalPath: `/wiki/${args.data.page.id}/edit`,
    };
  },

  render: ({ data }) => {
    const { authUser, page } = data;

    const t = useT();
    const turl = useTurl();
    const breadcrumbList = [
      { title: t({ en: "Wiki", ru: "Wiki" }), url: turl("/wiki") },

      {
        title: t({ en: "Page Editor", ru: "Редактор страницы" }),
        url: turl(`/wiki/${page.id}/edit`),
      },
    ];
    return (
      <PageEditor
        authUser={authUser}
        target={{ _: "wiki", pageId: page.id }}
        page={data.page}
        breadcrumbList={breadcrumbList}
      />
    );
  },
});
